import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import {
  EmailRequestType,
  LabelId,
  UserCommunication,
} from '@this+that/models';
import log from 'loglevel';

import { APP_PAGES } from '../../../routes/constants';
import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import { useUserCommunicationsStore } from '../../../store';
import { getLabelIdsForAction } from '../../../utils/emailLabelHelpers';
import { navigateToInboxOrDoBox } from '../../../utils/navigation';
import { useCustomToast } from '../../useCustomToast';

const INBOX_PREVIEW_PATH = '/inbox/preview';
const DOBOX_PREVIEW_PATH = '/do-box/preview';

export const useTrashUserCommunication = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    setDeleteLoading,
    updateUserCommunicationLabels,
    getUserCommunicationByMessageId,
  } = useUserCommunicationsStore();
  const { errorMessage, warningMessage } = useCustomToast();

  const handleTrashUserCommunication = useCallback(
    async (
      messageIds: string | string[],
      options?: {
        onSuccess?: () => void;
        currentTab?: string | null;
      },
    ) => {
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) {
        return;
      }

      setDeleteLoading(true);

      const communicationsToTrash = messageIdsArray
        .map((messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);
          if (!userCommunication) {
            log.error(
              `UserCommunication with messageId ${messageId} not found`,
            );
            return null;
          }
          return { messageId, communication: userCommunication };
        })
        .filter(
          (
            item,
          ): item is { messageId: string; communication: UserCommunication } =>
            item !== null,
        )
        .filter(
          ({ communication }) =>
            !communication.labelIds?.includes(LabelId.TRASH),
        );

      if (communicationsToTrash.length === 0) {
        warningMessage(t('inbox.userCommunication.actions.alreadyInTrash'));
        setDeleteLoading(false);
        return;
      }

      const labelBackupMap = new Map<string, LabelId[]>();
      communicationsToTrash.forEach(({ messageId, communication }) => {
        labelBackupMap.set(messageId, communication.labelIds || []);
        const newLabelIds = getLabelIdsForAction(
          communication.labelIds,
          'trash',
        );
        updateUserCommunicationLabels(messageId, newLabelIds);
      });

      if (window.location.pathname.includes(INBOX_PREVIEW_PATH)) {
        const currentTab = options?.currentTab ?? searchParams.get('tab');
        navigateToInboxOrDoBox(navigate, currentTab);
      } else if (window.location.pathname.includes(DOBOX_PREVIEW_PATH)) {
        navigate(APP_PAGES.DOBOX);
      }

      try {
        const operations = communicationsToTrash.map(
          async ({ messageId, communication }) => {
            try {
              if (!communication.userEmailAddress) {
                throw new Error('No user email address found in communication');
              }

              if (!communication.platform) {
                throw new Error('No platform found in communication');
              }

              await modifyEmail({
                userEmailAddress: communication.userEmailAddress,
                platform: communication.platform,
                messageId: communication.messageId,
                action: EmailRequestType.TRASH,
              });

              return { messageId, success: true, communication };
            } catch (error) {
              log.error(
                `Failed to trash email for messageId ${messageId}:`,
                error,
              );
              return { messageId, success: false, error, communication };
            }
          },
        );

        const results = await Promise.all(operations);
        const failed = results.filter((result) => !result.success);

        if (failed.length === 0) {
          log.info(
            `${communicationsToTrash.length} messages moved to trash successfully`,
          );
          options?.onSuccess?.();
        }

        if (failed.length > 0) {
          failed.forEach(({ messageId }) => {
            const previousLabels = labelBackupMap.get(messageId);
            if (previousLabels) {
              updateUserCommunicationLabels(messageId, previousLabels);
            }
          });

          log.warn(
            `${failed.length} operations failed out of ${communicationsToTrash.length}`,
          );

          errorMessage(
            failed.length === communicationsToTrash.length
              ? t('inbox.userCommunication.actions.trashError')
              : t('inbox.userCommunication.actions.partialTrashError', {
                  failed: failed.length,
                  total: communicationsToTrash.length,
                }),
          );
        }
      } catch (error) {
        communicationsToTrash.forEach(({ messageId }) => {
          const previousLabels = labelBackupMap.get(messageId);
          if (previousLabels) {
            updateUserCommunicationLabels(messageId, previousLabels);
          }
        });

        log.error(error);
        errorMessage(t('inbox.userCommunication.actions.trashError'));
      } finally {
        setDeleteLoading(false);
      }
    },
    [
      updateUserCommunicationLabels,
      setDeleteLoading,
      getUserCommunicationByMessageId,
      errorMessage,
      warningMessage,
      navigate,
      t,
      searchParams,
    ],
  );

  const extractMessageIdFromSelectedItems = useCallback(
    (selectedItems: Set<string>) => {
      return Array.from(selectedItems).map((messageId) => messageId);
    },
    [],
  );

  return {
    handleTrashUserCommunication,
    extractMessageIdFromSelectedItems,
  };
};
